import * as Yup from 'yup'
import { getContacts } from '../../../api/contacts/functions'
import { ContactCriteria } from '../../../api/contacts/types'
import { getEvidenceDiaries } from '../../../api/diaries/evidenceDiary/functions'
import { EvidenceDiaryListCriteria } from '../../../api/diaries/evidenceDiary/types'
import { getIntermediaryDiaries } from '../../../api/diaries/intermediaryDiary/functions'
import { IntermediaryDiaryListCriteria } from '../../../api/diaries/intermediaryDiary/types'
import { getDocuments } from '../../../api/documents/functions'
import { DocumentCriteria } from '../../../api/documents/types'
import { ContactType } from '../../../api/globalTypes'
import { getProjects } from '../../../api/projects/functions'
import { ProjectCriteria } from '../../../api/projects/types'
import { getProperties } from '../../../api/properties/functions'
import { PropertyCriteria } from '../../../api/properties/types'
import { DETAIL_SEARCH_TYPE } from '../../../common/constants'
import { companyDetailSearchInputs, companyProfileDetailSearchInputs } from '../../../common/forms/company/inputs'
import { companyDetailSearchValidation, companyProfileDetailSearchValidation } from '../../../common/forms/company/validations'
import { contactDetailSearchInputs, contactProfileDetailSearchInputs } from '../../../common/forms/contacts/inputs'
import { contactDetailSearchValidation, contactProfileDetailSearchValidation } from '../../../common/forms/contacts/validations'
import { documentsDetailSearchInputs } from '../../../common/forms/documents/inputs'
import { documentsDetailSearchValidation } from '../../../common/forms/documents/validations'
import { evidenceDiaryDetailSearchInputs } from '../../../common/forms/evidenceDiary/inputs'
import { evidenceDiaryDetailSearchValidation } from '../../../common/forms/evidenceDiary/validation'
import { propertyDetailSearchInputs } from '../../../common/forms/generalRealEstate/inputs'
import { propertyDetailSearchValidation } from '../../../common/forms/generalRealEstate/validations'
import { diaryDetailSearchInputs } from '../../../common/forms/intermediaryDiary/inputs'
import { diaryDetailSearchValidation } from '../../../common/forms/intermediaryDiary/validation'
import { projectDetailSearchInputs } from '../../../common/forms/projects/inputs'
import { projectDetailSearchValidation } from '../../../common/forms/projects/validation'
import {
    selectSearchCriteriaCompanies,
    selectSearchCriteriaContacts,
    selectSearchCriteriaDocuments,
    selectSearchCriteriaEvidenceDiaries,
    selectSearchCriteriaIntermediaryDiaries,
    selectSearchCriteriaProjects,
    selectSearchCriteriaProperties,
} from '../../../store/search/selectors'
import { transformContactDetailSearchCriteria } from './transforms/contact'
import { transformEvidenceDiaryDetailSearchCriteria } from './transforms/evidenceDiary'
import { transformPropertyDetailSearchCriteria } from './transforms/property'
import { filterEmptyDetailSearchValues } from './utils'

export interface DetailSearchValidationConfig {
    initialValues: Record<string, unknown>
    validationSchema?: Yup.AnyObjectSchema
    formGroup?: string
}

export interface CountResponse {
    count: number
}
// TODO: getInputs/getValidation are typed loosely because their implementations live in untyped
// common/forms/*/inputs.js and validation(s).js — tightening this needs those converted to .ts first.
export interface DetailSearchConfigEntry {
    titleKey: string
    subtitleKey?: string
    redirectPage: string
    hasInitialCriteria?: boolean
    selector: (state: any) => Record<string, unknown> | null
    fetchCount: (criteria: Record<string, unknown>) => Promise<CountResponse>
    transformCriteria: (values: Record<string, unknown>) => Record<string, unknown>
    getInputs: () => Record<string, unknown>
    getProfileInputs?: () => Record<string, unknown>
    getValidation: (data: Record<string, unknown> | null) => DetailSearchValidationConfig
    getProfileValidation?: (data: Record<string, unknown> | null) => DetailSearchValidationConfig
}

export type DetailSearchType = (typeof DETAIL_SEARCH_TYPE)[keyof typeof DETAIL_SEARCH_TYPE]

export const detailSearchConfig: Record<DetailSearchType, DetailSearchConfigEntry> = {
    [DETAIL_SEARCH_TYPE.PROJECT]: {
        titleKey: 'form.detailSearch.title.projects',
        redirectPage: 'projects',
        hasInitialCriteria: true,
        selector: selectSearchCriteriaProjects,
        fetchCount: (criteria) => getProjects({ criteria: criteria as ProjectCriteria, limit: 1 }),
        transformCriteria: (values) => filterEmptyDetailSearchValues(values),
        getInputs: () => projectDetailSearchInputs(),
        getValidation: (data) => projectDetailSearchValidation(data),
    },
    [DETAIL_SEARCH_TYPE.PROPERTY]: {
        titleKey: 'form.detailSearch.title.properties',
        redirectPage: 'properties',
        hasInitialCriteria: true,
        selector: selectSearchCriteriaProperties,
        fetchCount: (criteria) => getProperties({ criteria: criteria as PropertyCriteria, limit: 1 }),
        transformCriteria: transformPropertyDetailSearchCriteria,
        getInputs: () => propertyDetailSearchInputs(),
        getValidation: (data) => propertyDetailSearchValidation(data),
    },
    [DETAIL_SEARCH_TYPE.CONTACT]: {
        titleKey: 'form.detailSearch.title.contact',
        subtitleKey: 'form.detailSearch.subTitle.contact',
        redirectPage: 'contacts',
        selector: selectSearchCriteriaContacts,
        fetchCount: (criteria) => getContacts({ criteria: { ...criteria, contactType: ContactType.Person } as ContactCriteria, limit: 1 }),
        transformCriteria: transformContactDetailSearchCriteria,
        getInputs: () => contactDetailSearchInputs(),
        getProfileInputs: () => contactProfileDetailSearchInputs(),
        getValidation: (data) => contactDetailSearchValidation(data),
        getProfileValidation: (data) => contactProfileDetailSearchValidation(data),
    },
    [DETAIL_SEARCH_TYPE.COMPANY]: {
        titleKey: 'form.detailSearch.title.company',
        subtitleKey: 'form.detailSearch.subTitle.company',
        redirectPage: 'companies',
        selector: selectSearchCriteriaCompanies,
        fetchCount: (criteria) => getContacts({ criteria: { ...criteria, contactType: ContactType.Company } as ContactCriteria, limit: 1 }),
        transformCriteria: transformContactDetailSearchCriteria,
        getInputs: () => companyDetailSearchInputs(),
        getProfileInputs: () => companyProfileDetailSearchInputs(),
        getValidation: (data) => companyDetailSearchValidation(data),
        getProfileValidation: (data) => companyProfileDetailSearchValidation(data),
    },
    [DETAIL_SEARCH_TYPE.DOCUMENT]: {
        titleKey: 'form.detailSearch.title.documents',
        redirectPage: 'documents',
        selector: selectSearchCriteriaDocuments,
        fetchCount: (criteria) => getDocuments('', { criteria: criteria as DocumentCriteria, collection: 'document', limit: 1 }),
        transformCriteria: (values) => filterEmptyDetailSearchValues(values),
        getInputs: () => documentsDetailSearchInputs(),
        getValidation: (data) => documentsDetailSearchValidation(data),
    },
    [DETAIL_SEARCH_TYPE.INTERMEDIARY_DIARY]: {
        titleKey: 'form.detailSearch.title.diary',
        redirectPage: 'intermediary-diary',
        selector: selectSearchCriteriaIntermediaryDiaries,
        fetchCount: (criteria) => getIntermediaryDiaries({ criteria: criteria as IntermediaryDiaryListCriteria, limit: 1 }),
        transformCriteria: (values) => filterEmptyDetailSearchValues(values),
        getInputs: () => diaryDetailSearchInputs(),
        getValidation: (data) => diaryDetailSearchValidation(data),
    },
    [DETAIL_SEARCH_TYPE.EVIDENCE_DIARY]: {
        titleKey: 'form.detailSearch.title.evidenceDiary',
        redirectPage: 'evidence-diary',
        selector: selectSearchCriteriaEvidenceDiaries,
        fetchCount: (criteria) => getEvidenceDiaries({ criteria: criteria as EvidenceDiaryListCriteria, limit: 1 }),
        transformCriteria: transformEvidenceDiaryDetailSearchCriteria,
        getInputs: () => evidenceDiaryDetailSearchInputs(),
        getValidation: (data) => evidenceDiaryDetailSearchValidation(data),
    },
}

export const getDetailSearchConfig = (type: DetailSearchType): DetailSearchConfigEntry => {
    const config = detailSearchConfig[type]

    if (!config) {
        throw new Error(`Unknown detail search type: ${type}`)
    }

    return config
}
